/**
 * Homeowner service activity — shows scheduled/completed visits
 * for the property. Only safe fields exposed via get_homeowner_visits RPC.
 */

import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Wrench, Calendar, CheckCircle2, Clock } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

const STATUS_DISPLAY: Record<string, { label: string; icon: typeof Clock; className: string }> = {
  scheduled: { label: 'Scheduled', icon: Calendar, className: 'bg-blue-500/15 text-blue-500 border-blue-500/30' },
  in_progress: { label: 'In progress', icon: Clock, className: 'bg-wt-amber-primary/15 text-wt-amber-primary border-wt-amber-primary/30' },
  completed: { label: 'Completed', icon: CheckCircle2, className: 'bg-wt-green-primary/15 text-wt-green-primary border-wt-green-primary/30' },
};

const VISIT_TYPE_LABELS: Record<string, string> = {
  preventive: 'Preventive maintenance',
  repair: 'Repair visit',
  emergency: 'Emergency service',
  commissioning: 'System commissioning',
  warranty_inspection: 'Warranty inspection',
};

interface HomeownerVisit {
  id: string;
  property_id: string;
  visit_type: string;
  status: string;
  scheduled_at: string | null;
  completed_at: string | null;
  technician_name: string | null;
  customer_notes: string | null;
}

interface HomeownerServiceActivityProps {
  propertyId: string;
}

export function HomeownerServiceActivity({ propertyId }: HomeownerServiceActivityProps) {
  const { data: visits, isLoading } = useQuery({
    queryKey: ['homeowner-visits', propertyId],
    queryFn: async (): Promise<HomeownerVisit[]> => {
      const { data, error } = await supabase
        .rpc('get_homeowner_visits', { p_property_id: propertyId, p_limit: 10 });

      if (error) throw error;
      return (data ?? []) as HomeownerVisit[];
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card className="border-border/50">
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <Wrench className="w-4 h-4 text-muted-foreground" />
          Service Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!visits || visits.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">
            No service visits yet. If we schedule service for your property, it will appear here.
          </p>
        ) : (
          <div className="space-y-2">
            {visits.map((visit) => {
              const statusCfg = STATUS_DISPLAY[visit.status] ?? STATUS_DISPLAY.scheduled;
              const StatusIcon = statusCfg.icon;
              const typeLabel = VISIT_TYPE_LABELS[visit.visit_type] ?? visit.visit_type;

              const dateStr = visit.status === 'completed' && visit.completed_at
                ? `Completed ${formatDistanceToNow(new Date(visit.completed_at), { addSuffix: true })}`
                : visit.scheduled_at
                  ? `${format(new Date(visit.scheduled_at), 'MMM d, yyyy')}`
                  : 'Date TBD';

              return (
                <div
                  key={visit.id}
                  className="p-2.5 rounded-md border border-border/50 bg-wt-bg-tertiary/20 space-y-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{typeLabel}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{dateStr}</span>
                        {visit.technician_name && (
                          <>
                            <span className="text-border">·</span>
                            <span>{visit.technician_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn('text-[10px] shrink-0 flex items-center gap-1', statusCfg.className)}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {statusCfg.label}
                    </Badge>
                  </div>
                  {visit.customer_notes && (
                    <p className="text-xs text-muted-foreground border-t border-border/30 pt-1 mt-1">
                      {visit.customer_notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
